import { useState, useEffect } from 'react'
import lodash from 'lodash'
import { Site } from '../../../types/Site'
import { SiteTypeExpertise } from '../../../types/SiteTypeExpertise'
import { TypeExpertise } from '../../../enums/TypeExpertise'
import BackOfficeAPIClient from '../../../clients/BackofficeAPIClient'

const backofficeApiClient = new BackOfficeAPIClient()

interface UseInspectionsReturn {
    sites: Site[]
    inspections: SiteTypeExpertise[]
    hasChanges: boolean
    submitting: boolean
    toggleExpertise: (siteId: number, inspectionType: TypeExpertise, enable: boolean) => void
    isInspectionActive: (siteId: number, inspectionType: TypeExpertise) => boolean
    save: () => Promise<void>
}

export function useInspections(): UseInspectionsReturn {
    const [sites, setSites] = useState<Site[]>([])
    const [inspections, setInspections] = useState<SiteTypeExpertise[]>([])
    const [hasChanges, setHasChanges] = useState(false)
    const [submitting, setSubmitting] = useState(false)

    useEffect(() => {
        fetchSites()
        fetchInspections()
    }, [])

    const fetchSites = async () => {
        try {
            let data = await backofficeApiClient.get<Site[]>('/sites')
            data = lodash.sortBy(data, (site: Site) => site.Plaque_Libelle)
            setSites(data)
        } catch (error) {
            console.log(error)
        }
    }

    const fetchInspections = async () => {
        try {
            const data = await backofficeApiClient.get<SiteTypeExpertise[]>('/siteTypeExpertise')
            setInspections(data)
        } catch (error) {
            console.log(error)
        }
    }

    const toggleExpertise = (siteId: number, inspectionType: TypeExpertise, enable: boolean) => {
        const copy = lodash.cloneDeep(inspections)
        const index = copy.findIndex(e => e.site_id === siteId && e.inspection_type === inspectionType)

        if (enable) {
            if (index === -1) {
                copy.push(new SiteTypeExpertise({ site_id: siteId, inspection_type: inspectionType, IDCompany: 1 }))
            }
        } else {
            if (index !== -1) copy.splice(index, 1)
        }

        setHasChanges(true)
        setInspections(copy)
    }

    const isInspectionActive = (siteId: number, inspectionType: TypeExpertise): boolean => {
        return inspections.some(e => e.site_id === siteId && e.inspection_type === inspectionType)
    }

    const save = async () => {
        setSubmitting(true)
        try {
            await backofficeApiClient.post('/siteTypeExpertise', { expertises: inspections })
            setHasChanges(false)
        } catch (error) {
            console.log(error)
        } finally {
            setSubmitting(false)
        }
    }

    return { sites, inspections, hasChanges, submitting, toggleExpertise, isInspectionActive, save }
}
